import { useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function PrivacyPolicyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);

    // Set page title and meta tags
    const prevTitle = document.title;
    document.title = 'Privacy Policy | Hoosier Life Insurance';

    const setMeta = (name: string, content: string, prop = false) => {
      const selector = prop ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let el = document.querySelector(selector) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        prop ? el.setAttribute('property', name) : el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const setCanonical = (href: string) => {
      let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', 'canonical');
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
    };

    setMeta('description', 'Privacy Policy for Hoosier Life Insurance. Learn how we collect, use, and protect your personal information, including SMS consent and data security practices.');
    setMeta('keywords', 'privacy policy, data protection, SMS consent, TCPA compliance, insurance privacy');
    setMeta('robots', 'index, follow');
    setCanonical(`${import.meta.env.VITE_SITE_URL || 'https://hoosierlifeinsurance.com'}/privacy-policy`);
    setMeta('og:title', 'Privacy Policy | Hoosier Life Insurance', true);
    setMeta('og:description', 'Privacy Policy for Hoosier Life Insurance. Learn how we collect, use, and protect your personal information.', true);
    setMeta('og:url', `${import.meta.env.VITE_SITE_URL || 'https://hoosierlifeinsurance.com'}/privacy-policy`, true);
    setMeta('twitter:title', 'Privacy Policy | Hoosier Life Insurance');
    setMeta('twitter:description', 'Read the Hoosier Life Insurance privacy policy. Learn how we collect, use, and protect your personal information.');
    setMeta('last-modified', new Date().toISOString());

    // Inject Schema.org JSON-LD
    const schemaData = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Privacy Policy',
      description: 'Privacy Policy for Hoosier Life Insurance. Learn how we collect, use, and protect your personal information, including SMS consent and data security practices.',
      url: `${import.meta.env.VITE_SITE_URL || 'https://hoosierlifeinsurance.com'}/privacy-policy`,
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: import.meta.env.VITE_SITE_URL || 'https://hoosierlifeinsurance.com',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Privacy Policy',
            item: `${import.meta.env.VITE_SITE_URL || 'https://hoosierlifeinsurance.com'}/privacy-policy`,
          },
        ],
      },
    };

    let schemaEl = document.getElementById('schema-privacy-policy');
    if (!schemaEl) {
      schemaEl = document.createElement('script');
      schemaEl.id = 'schema-privacy-policy';
      (schemaEl as HTMLScriptElement).type = 'application/ld+json';
      document.head.appendChild(schemaEl);
    }
    schemaEl.textContent = JSON.stringify(schemaData);

    return () => {
      document.title = prevTitle;
      document.getElementById('schema-privacy-policy')?.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-[#1e3a5f] text-white py-6 sticky top-0 z-50 shadow-md">
        <div className="max-w-5xl mx-auto px-6 flex items-center justify-between">
          <Link 
            to="/" 
            className="flex items-center gap-3 hover:opacity-80 transition-opacity"
            aria-label="Return to Hoosier Life Insurance homepage"
          >
            <div className="w-10 h-10 flex items-center justify-center">
              <i className="ri-shield-check-line text-3xl text-[#D97642]"></i>
            </div>
            <span className="text-xl font-bold">Hoosier Life Insurance</span>
          </Link>
          <Link 
            to="/" 
            className="text-sm hover:text-[#ff6b35] transition-colors flex items-center gap-2"
            aria-label="Navigate back to homepage"
          >
            <i className="ri-home-line"></i>
            Back to Home
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Title Section */}
        <div className="mb-12">
          <h1 className="text-4xl font-bold text-[#1e3a5f] mb-4">Privacy Policy</h1>
          <p className="text-gray-600 text-sm">
            <strong>Last Updated:</strong> January 14, 2025
          </p>
        </div>

        {/* Content Sections */}
        <div className="space-y-10 text-gray-700 leading-relaxed">
          
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">1. Introduction</h2>
            <p className="mb-4">
              Welcome to Hoosier Life Insurance ("we," "our," or "us"). We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website, use our services, or communicate with us through various channels including SMS text messaging, email, and phone.
            </p>
            <p>
              By using our website or services, you agree to the collection and use of information in accordance with this Privacy Policy. If you do not agree with our policies and practices, please do not use our services.
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">2. Information We Collect</h2>
            <p className="mb-4">We collect several types of information from and about users of our services:</p>
            
            <h3 className="text-xl font-semibold text-[#1e3a5f] mb-3 mt-6">Personal Information</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>Contact Information:</strong> Name, email address, phone number, mailing address</li>
              <li><strong>Demographic Information:</strong> Age, date of birth, gender, marital status</li>
              <li><strong>Financial Information:</strong> Income level, employment status, insurance needs and preferences</li>
              <li><strong>Health Information:</strong> General health status, smoking status, medical history (only when necessary for insurance quotes)</li>
              <li><strong>Communication Records:</strong> Records of your communications with us via phone, email, SMS, or other channels</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#1e3a5f] mb-3 mt-6">Automatically Collected Information</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Device Information:</strong> IP address, browser type, operating system, device identifiers</li>
              <li><strong>Usage Data:</strong> Pages visited, time spent on pages, links clicked, referring website</li>
              <li><strong>Location Data:</strong> General geographic location based on IP address</li>
              <li><strong>Cookies and Tracking Technologies:</strong> Information collected through cookies, web beacons, and similar technologies</li>
            </ul>
          </section>

          {/* How We Use Your Information */}
          <section>
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">3. How We Use Your Information</h2>
            <p className="mb-4">We use the information we collect for the following purposes:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Provide Services:</strong> To provide insurance quotes, policy recommendations, and related services</li>
              <li><strong>Communication:</strong> To respond to your inquiries, send appointment reminders, and provide customer support</li>
              <li><strong>Marketing:</strong> To send promotional materials, newsletters, and information about products and services that may interest you</li>
              <li><strong>Improvement:</strong> To analyze usage patterns and improve our website, services, and customer experience</li>
              <li><strong>Compliance:</strong> To comply with legal obligations, enforce our terms, and protect our rights</li>
              <li><strong>Personalization:</strong> To personalize your experience and deliver content relevant to your interests</li>
            </ul>
          </section>

          {/* SMS/Text Message Consent */}
          <section id="sms" className="bg-orange-50 border-l-4 border-[#ff6b35] p-6 rounded-r-lg">
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">4. SMS/Text Message Consent &amp; A2P Compliance</h2>

            <h3 className="text-xl font-semibold text-[#1e3a5f] mb-3">How We Obtain Consent</h3>
            <p className="mb-4">
              We only send SMS/text messages to individuals who have <strong>explicitly opted in</strong> by checking the SMS consent checkbox on our website forms (Free Quote form, Contact form, or any other lead form). Checking that box constitutes your written consent under the Telephone Consumer Protection Act (TCPA) and A2P 10DLC regulations. <strong>Consent is not a condition of purchase.</strong> You may still receive our services without agreeing to receive text messages.
            </p>

            <h3 className="text-xl font-semibold text-[#1e3a5f] mb-3 mt-6">Opt-In Consent</h3>
            <p className="mb-4">
              By checking the SMS consent checkbox and submitting a form on this website, you expressly consent to receive automated marketing text messages, appointment reminders, policy updates, and other service-related communications from <strong>Hoosier Life Insurance</strong> at the mobile phone number you provided.
            </p>

            <h3 className="text-xl font-semibold text-[#1e3a5f] mb-3 mt-6">Message Frequency</h3>
            <p className="mb-4">
              Message frequency varies depending on your interaction with our services. You may receive up to <strong>10 messages per month</strong>, including promotional offers, appointment reminders, policy information, and service updates.
            </p>

            <h3 className="text-xl font-semibold text-[#1e3a5f] mb-3 mt-6">Message and Data Rates</h3>
            <p className="mb-4">
              <strong>Message and data rates may apply</strong> based on your mobile carrier's plan. Please contact your wireless carrier for details about your specific plan and any applicable charges.
            </p>

            <h3 className="text-xl font-semibold text-[#1e3a5f] mb-3 mt-6">Opt-Out Instructions</h3>
            <p className="mb-4">
              You may opt out of receiving text messages at any time by:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Replying <strong>STOP</strong>, <strong>END</strong>, <strong>CANCEL</strong>, <strong>UNSUBSCRIBE</strong>, or <strong>QUIT</strong> to any text message you receive from us</li>
              <li>Contacting us directly at <strong>(317) 648-2983</strong> or <strong>info@hoosierlifeinsurance.com</strong></li>
            </ul>
            <p className="mb-4">
              After you opt out, you will receive one final confirmation message. You may continue to receive non-marketing messages related to active insurance policies or pending transactions.
            </p>

            <h3 className="text-xl font-semibold text-[#1e3a5f] mb-3 mt-6">Help and Support</h3>
            <p className="mb-4">
              For help or more information about our text messaging program, reply <strong>HELP</strong> to any message or contact us at <strong>(317) 648-2983</strong>.
            </p>

            <h3 className="text-xl font-semibold text-[#1e3a5f] mb-3 mt-6">No Sharing of Phone Numbers</h3>
            <p className="mb-4">
              We do not sell, rent, or share your mobile phone number with third parties for their own marketing purposes. Phone numbers collected for SMS communications are used solely by Hoosier Life Insurance to deliver the messages you consented to receive.
            </p>

            <h3 className="text-xl font-semibold text-[#1e3a5f] mb-3 mt-6">Carrier Disclaimer</h3>
            <p>
              Carriers are not liable for delayed or undelivered messages. Supported carriers include AT&amp;T, T-Mobile, Verizon Wireless, Sprint, Boost, U.S. Cellular, and other major carriers. Service may not be available on all carriers.
            </p>
          </section>

          {/* Email Communications */}
          <section>
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">5. Email Communications</h2>
            <p className="mb-4">
              When you provide your email address, you may receive:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Service-related emails (quote confirmations, appointment reminders, policy updates)</li>
              <li>Marketing emails (newsletters, promotional offers, educational content)</li>
              <li>Transactional emails (password resets, account notifications)</li>
            </ul>
            <p className="mb-4">
              You can unsubscribe from marketing emails at any time by clicking the "unsubscribe" link at the bottom of any marketing email or by contacting us directly. Please note that you may still receive service-related emails even after unsubscribing from marketing communications.
            </p>
          </section>

          {/* Sharing of Information */}
          <section>
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">6. Sharing of Information</h2>
            <p className="mb-4">We may share your information with:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Insurance Carriers:</strong> To obtain quotes and process insurance applications on your behalf</li>
              <li><strong>Service Providers:</strong> Third-party vendors who perform services on our behalf (CRM systems, email platforms, SMS providers, analytics services)</li>
              <li><strong>Business Partners:</strong> Trusted partners who may offer products or services that complement ours</li>
              <li><strong>Legal Requirements:</strong> When required by law, court order, or government regulation</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
              <li><strong>Protection of Rights:</strong> To protect our rights, property, safety, or that of our users or others</li>
            </ul>
            <p className="mt-4">
              We do not sell your personal information to third parties for their marketing purposes without your explicit consent.
            </p>
          </section>

          {/* Data Security */}
          <section>
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">7. Data Security</h2>
            <p className="mb-4">
              We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Encryption of data in transit and at rest</li>
              <li>Regular security assessments and updates</li>
              <li>Access controls and authentication procedures</li>
              <li>Employee training on data protection and privacy</li>
              <li>Secure data storage and backup systems</li>
            </ul>
            <p>
              However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
            </p>
          </section>

          {/* Cookies & Tracking */}
          <section>
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">8. Cookies & Tracking Technologies</h2>
            <p className="mb-4">
              We use cookies, web beacons, and similar tracking technologies to collect information about your browsing activities and preferences. These technologies help us:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Remember your preferences and settings</li>
              <li>Understand how you use our website</li>
              <li>Improve website performance and user experience</li>
              <li>Deliver targeted advertising and measure campaign effectiveness</li>
              <li>Analyze traffic patterns and trends</li>
            </ul>
            <p className="mb-4">
              You can control cookies through your browser settings. However, disabling cookies may limit your ability to use certain features of our website.
            </p>
          </section>

          {/* Your Rights */}
          <section>
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">9. Your Rights</h2>
            <p className="mb-4">Depending on your location, you may have the following rights regarding your personal information:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>Access:</strong> Request access to the personal information we hold about you</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal obligations)</li>
              <li><strong>Opt-Out:</strong> Opt out of marketing communications, SMS messages, and certain data processing activities</li>
              <li><strong>Data Portability:</strong> Request a copy of your data in a portable format</li>
              <li><strong>Restriction:</strong> Request restriction of processing in certain circumstances</li>
            </ul>
            <p>
              To exercise any of these rights, please contact us using the information provided in the Contact section below.
            </p>
          </section>

          {/* Children's Privacy */}
          <section>
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">10. Children's Privacy</h2>
            <p>
              Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately so we can delete the information.
            </p>
          </section>

          {/* Changes to Privacy Policy */}
          <section>
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">11. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. We will notify you of any material changes by posting the updated policy on our website with a new "Last Updated" date. Your continued use of our services after such changes constitutes acceptance of the updated policy.
            </p>
          </section>

          {/* Contact Information */}
          <section className="bg-gray-50 p-6 rounded-lg">
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">12. Contact Information</h2>
            <p className="mb-4">
              If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="space-y-2 text-gray-700">
              <p><strong>Hoosier Life Insurance</strong></p>
              <p>5625 German Church Road, Indianapolis, IN 46235</p>
              <p className="mt-4">
                <strong>Phone:</strong> <a href="tel:3176482983" className="text-[#ff6b35] hover:underline">(317) 648-2983</a>
              </p>
              <p>
                <strong>Email:</strong> <a href="mailto:info@hoosierlifeinsurance.com" className="text-[#ff6b35] hover:underline">info@hoosierlifeinsurance.com</a>
              </p>
            </div>
          </section>

        </div>

        {/* Back to Top */}
        <div className="mt-12 pt-8 border-t border-gray-200 text-center">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="inline-flex items-center gap-2 text-[#1e3a5f] hover:text-[#ff6b35] transition-colors font-medium"
          >
            <i className="ri-arrow-up-line"></i>
            Back to Top
          </button>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#1e3a5f] text-white py-8 mt-16">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <p className="text-sm text-gray-300">
            © {new Date().getFullYear()} Hoosier Life Insurance. All rights reserved.
          </p>
          <div className="mt-4 flex items-center justify-center gap-6 text-sm">
            <Link to="/privacy-policy" className="text-gray-300 hover:text-[#ff6b35] transition-colors">
              Privacy Policy
            </Link>
            <span className="text-gray-500">|</span>
            <Link to="/terms-of-service" className="text-gray-300 hover:text-[#ff6b35] transition-colors">
              Terms of Service
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}